/**
 * v4 Files — central document hub with v1 feature parity:
 *   • Snap-receipt CTA (with AI badge)
 *   • "Files your accountant requested" tracker
 *   • Search across filename + category
 *   • Category chips + Year filter
 *   • Grouped file list by FY
 *   • Preview modal with download/open actions
 *   • Per-file "Ask about this" → focused AI chat
 *   • Add-file sheet (camera / email / WhatsApp)
 */

import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Download, MessageCircleQuestion, FileText, Mail, Receipt as ReceiptIcon,
  Landmark, BarChart3, Plus, X, ChevronRight, Camera, MessageCircle as WhatsAppIcon,
  Sparkles, Upload, AlertCircle, CheckCircle2, Eye, Mic, MessageSquareText, Send,
} from 'lucide-react';
import { useV4 } from './i18n';
import { cn } from '@/lib/utils';

type Category = 'tax' | 'bas' | 'financials' | 'letters' | 'receipts' | 'other';

interface FileRow {
  id: string; filename: string; category: Category;
  fy: string; date: string; pages: number; sizeKb: number;
  preparedBy?: string; important?: boolean;
}

const FILES: FileRow[] = [
  { id: 'f1',  filename: 'BAS Q3 2025-26 (draft).pdf',         category: 'bas',         fy: '2025-26', date: '12 May 2026', pages: 6,  sizeKb: 124, preparedBy: 'Marcus Bell', important: true },
  { id: 'f2',  filename: 'Balance Sheet — 30 Apr 2026.pdf',    category: 'financials',  fy: '2025-26', date: '5 May 2026',  pages: 4,  sizeKb: 89,  preparedBy: 'Marcus Bell' },
  { id: 'f3',  filename: 'P&L YTD — Apr 2026.pdf',             category: 'financials',  fy: '2025-26', date: '5 May 2026',  pages: 8,  sizeKb: 142, preparedBy: 'Marcus Bell' },
  { id: 'f4',  filename: 'BAS Q2 2025-26 — lodged.pdf',        category: 'bas',         fy: '2025-26', date: '28 Feb 2026', pages: 6,  sizeKb: 118, preparedBy: 'Marcus Bell' },
  { id: 'f5',  filename: 'BAS Q1 2025-26 — lodged.pdf',        category: 'bas',         fy: '2025-26', date: '28 Nov 2025', pages: 6,  sizeKb: 116, preparedBy: 'Marcus Bell' },
  { id: 'f6',  filename: 'ATO — BAS reminder.pdf',             category: 'letters',     fy: '2025-26', date: '12 May 2026', pages: 1,  sizeKb: 38 },
  { id: 'f7',  filename: 'ASIC — Annual review.pdf',           category: 'letters',     fy: '2025-26', date: '3 May 2026',  pages: 2,  sizeKb: 64 },
  { id: 'f8',  filename: 'Bunnings receipt — 234.50.pdf',      category: 'receipts',    fy: '2025-26', date: '15 May 2026', pages: 1,  sizeKb: 28 },
  { id: 'f9',  filename: 'Smith Electrical invoice — 1,240.pdf', category: 'receipts',  fy: '2025-26', date: '15 May 2026', pages: 1,  sizeKb: 34 },
  { id: 'f10', filename: 'Tax Return FY2024-25.pdf',           category: 'tax',         fy: '2024-25', date: '12 Oct 2025', pages: 12, sizeKb: 230, preparedBy: 'Marcus Bell', important: true },
  { id: 'f11', filename: 'Financial Statements FY2024-25.pdf', category: 'financials',  fy: '2024-25', date: '12 Oct 2025', pages: 14, sizeKb: 248, preparedBy: 'Marcus Bell' },
  { id: 'f12', filename: 'Balance Sheet — 30 Jun 2025.pdf',    category: 'financials',  fy: '2024-25', date: '8 Oct 2025',  pages: 4,  sizeKb: 92,  preparedBy: 'Marcus Bell' },
  { id: 'f13', filename: 'Notice of Assessment 2024-25.pdf',   category: 'tax',         fy: '2024-25', date: '20 Oct 2025', pages: 2,  sizeKb: 56 },
  { id: 'f14', filename: 'BAS Q4 2024-25 — lodged.pdf',        category: 'bas',         fy: '2024-25', date: '28 Aug 2025', pages: 6,  sizeKb: 122, preparedBy: 'Marcus Bell' },
  { id: 'f15', filename: 'BAS Q3 2024-25 — lodged.pdf',        category: 'bas',         fy: '2024-25', date: '28 May 2025', pages: 6,  sizeKb: 118, preparedBy: 'Marcus Bell' },
  { id: 'f16', filename: 'Tax Return FY2023-24.pdf',           category: 'tax',         fy: '2023-24', date: '14 Sep 2024', pages: 12, sizeKb: 224, preparedBy: 'Marcus Bell' },
  { id: 'f17', filename: 'Financial Statements FY2023-24.pdf', category: 'financials',  fy: '2023-24', date: '14 Sep 2024', pages: 14, sizeKb: 242, preparedBy: 'Marcus Bell' },
  { id: 'f18', filename: 'Trust Deed.pdf',                     category: 'other',       fy: '2023-24', date: '5 Jul 2023',  pages: 28, sizeKb: 412 },
  { id: 'f19', filename: 'Tax Return FY2022-23.pdf',           category: 'tax',         fy: '2022-23', date: '20 Aug 2023', pages: 11, sizeKb: 218, preparedBy: 'Marcus Bell' },
  { id: 'f20', filename: 'Insurance — Public Liability.pdf',   category: 'other',       fy: '2022-23', date: '1 Jul 2022',  pages: 4,  sizeKb: 86 },
];

const CATEGORIES: { key: Category | 'all'; tKey: string }[] = [
  { key: 'all',        tKey: 'category_all' },
  { key: 'tax',        tKey: 'category_tax' },
  { key: 'bas',        tKey: 'category_bas' },
  { key: 'financials', tKey: 'category_financials' },
  { key: 'letters',    tKey: 'category_letters' },
  { key: 'receipts',   tKey: 'category_receipts' },
  { key: 'other',      tKey: 'category_other' },
];

const CAT_STYLE: Record<Category, { icon: any; bg: string; text: string; ring: string }> = {
  tax:        { icon: Landmark,    bg: 'bg-rose-50 dark:bg-rose-950/40',       text: 'text-rose-700 dark:text-rose-300',       ring: 'ring-rose-200 dark:ring-rose-900/60' },
  bas:        { icon: FileText,    bg: 'bg-amber-50 dark:bg-amber-950/40',     text: 'text-amber-700 dark:text-amber-300',     ring: 'ring-amber-200 dark:ring-amber-900/60' },
  financials: { icon: BarChart3,   bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-200 dark:ring-emerald-900/60' },
  letters:    { icon: Mail,        bg: 'bg-[var(--v4-accent-tint)]',           text: 'text-[var(--v4-accent-text)]',           ring: 'ring-[var(--v4-accent)]/30' },
  receipts:   { icon: ReceiptIcon, bg: 'bg-teal-50 dark:bg-teal-950/40',       text: 'text-teal-700 dark:text-teal-300',       ring: 'ring-teal-200 dark:ring-teal-900/60' },
  other:      { icon: FileText,    bg: 'bg-muted',                             text: 'text-foreground/80',                     ring: 'ring-border' },
};

interface RequestedFile {
  id: string; title: string; description: string; dueDate: string; status: 'pending' | 'uploaded';
}

const INITIAL_REQUESTS: RequestedFile[] = [
  { id: 'r1', title: 'Q4 bank statement',    description: 'CommBank business · Jan–Mar 2026', dueDate: 'Due 30 May',  status: 'pending' },
  { id: 'r2', title: 'Vehicle logbook',      description: '12 weeks ending May 2026',          dueDate: 'Due 5 Jun',   status: 'pending' },
  { id: 'r3', title: 'Equipment receipts',   description: 'Assets > $1,000 from March',         dueDate: 'Uploaded 8 May', status: 'uploaded' },
];

export default function FilesPageV4() {
  const { t, dir } = useV4();
  const [category, setCategory] = useState<Category | 'all'>('all');
  const [year, setYear] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [preview, setPreview] = useState<FileRow | null>(null);
  const [requests, setRequests] = useState<RequestedFile[]>(INITIAL_REQUESTS);

  const years = useMemo(() => Array.from(new Set(FILES.map(f => f.fy))).sort().reverse(), []);

  const filtered = useMemo(() => FILES.filter(f => {
    if (category !== 'all' && f.category !== category) return false;
    if (year !== 'all' && f.fy !== year) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!f.filename.toLowerCase().includes(q) && !f.category.includes(q)) return false;
    }
    return true;
  }), [category, year, search]);

  const grouped = useMemo(() => {
    const out: Record<string, FileRow[]> = {};
    for (const f of filtered) (out[f.fy] = out[f.fy] || []).push(f);
    return out;
  }, [filtered]);

  const markUploaded = (id: string) => {
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'uploaded', dueDate: t('uploaded_just_now') } : r));
  };
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-3 pb-4">
      <div className="pt-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t('files_title')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t('files_subtitle')}</p>
      </div>

      {/* Send-to-firm command hub — the central communication channel */}
      <div className="relative overflow-hidden rounded-lg border border-[var(--v4-accent)]/30 bg-[var(--v4-accent-tint)] p-5">
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-[var(--v4-accent)]/15 blur-3xl" />

        <div className="relative">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-[var(--v4-accent)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
              <Sparkles className="h-2.5 w-2.5" /> AI
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--v4-accent-text)]">
              Direct line to {t('firm_initials')}
            </span>
          </div>
          <h2 className="text-lg font-bold text-foreground leading-tight">{t('snap_receipt')}</h2>
          <p className="mt-1 text-xs text-foreground/80 leading-relaxed">{t('snap_hint')}</p>

          {/* 4 send-action buttons — full visible inline, not hidden in a sheet */}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <SendAction onClick={() => setShowAdd(true)} icon={Camera}      label={t('opt_camera')}  />
            <SendAction onClick={() => setShowAdd(true)} icon={Upload}      label={t('opt_upload')}  />
            <SendAction onClick={() => setShowAdd(true)} icon={Mic}         label={t('opt_voice')}   />
            <SendAction onClick={() => setShowAdd(true)} icon={MessageSquareText} label={t('opt_message')} />
          </div>

          {/* Why use this */}
          <div className="mt-4 flex items-start gap-2 rounded-md border border-[var(--v4-accent)]/20 bg-card/60 p-2.5">
            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[var(--v4-accent-text)] mt-0.5" />
            <div className="text-[11px] leading-snug">
              <p className="font-semibold text-foreground">{t('send_response_time')}</p>
              <p className="mt-0.5 text-muted-foreground">{t('send_tagline')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Requested files */}
      {requests.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/30 p-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500 dark:bg-amber-600 text-white shadow-sm">
              <Upload className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">{t('requested_files')}</p>
              {pendingCount > 0 && <p className="text-[11px] text-amber-700 dark:text-amber-300">{pendingCount} {t('pending')}</p>}
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {requests.map(r => (
              <div key={r.id} className={cn('flex items-center gap-3 rounded-md bg-card p-3 border border-amber-100 dark:border-amber-900/40', r.status === 'uploaded' && 'opacity-75')}>
                <div className={cn('flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
                  r.status === 'uploaded' ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300' : 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300')}>
                  {r.status === 'uploaded' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{r.title}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{r.description}</p>
                  <p className={cn('mt-0.5 text-[10px] font-semibold uppercase tracking-wider',
                    r.status === 'uploaded' ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300')}>
                    {r.dueDate}
                  </p>
                </div>
                {r.status === 'pending' && (
                  <button
                    onClick={() => markUploaded(r.id)}
                    className="flex-shrink-0 rounded-md bg-[var(--v4-accent)] px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-[var(--v4-accent-hover)]"
                  >
                    {t('upload')}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className={cn('absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60', dir === 'rtl' ? 'right-3' : 'left-3')} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('search_files')}
          className={cn(
            'w-full rounded-md border border-border bg-card py-2.5 text-sm placeholder:text-muted-foreground/60 focus:border-[var(--v4-accent)] focus:outline-none',
            dir === 'rtl' ? 'pr-9 pl-3' : 'pl-9 pr-3'
          )}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className={cn('absolute top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground/80', dir === 'rtl' ? 'left-3' : 'right-3')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Category — underline tab bar + Year dropdown on the right */}
      <div className="flex items-center justify-between gap-2 border-b border-border">
        <nav className="-mb-px flex flex-1 gap-4 overflow-x-auto">
          {CATEGORIES.map(c => {
            const count = c.key === 'all' ? FILES.length : FILES.filter(f => f.category === c.key).length;
            const active = category === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setCategory(c.key as any)}
                className={cn(
                  'flex-shrink-0 border-b-2 px-0.5 py-2 text-xs font-semibold whitespace-nowrap transition-colors',
                  active
                    ? 'border-[var(--v4-accent)] text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground/80'
                )}
              >
                {t(c.tKey)}
                <span className={cn(
                  'ml-1.5 rounded-md px-1.5 py-0.5 text-[10px] tabular-nums',
                  active ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground'
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </nav>
        <select
          value={year}
          onChange={e => setYear(e.target.value)}
          className="flex-shrink-0 -mb-px rounded-md border border-border bg-card px-2 py-1.5 text-xs font-semibold text-foreground/80 focus:border-foreground focus:outline-none"
        >
          <option value="all">{t('all_years')}</option>
          {years.map(y => <option key={y} value={y}>FY {y}</option>)}
        </select>
      </div>

      {/* Files grouped */}
      {Object.keys(grouped).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card p-12 text-center">
          <FileText className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 text-sm text-muted-foreground">{t('no_files_match')}</p>
        </div>
      ) : (
        Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0])).map(([fy, rows]) => (
          <div key={fy}>
            <p className="px-1 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground/60">FY {fy}</p>
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              <ul className="divide-y divide-border/60">
                {rows.map(f => <FileRowItem key={f.id} file={f} t={t} dir={dir} onPreview={() => setPreview(f)} />)}
              </ul>
            </div>
          </div>
        ))
      )}

      {showAdd && <AddFileSheet onClose={() => setShowAdd(false)} t={t} />}
      {preview && <PreviewModal file={preview} onClose={() => setPreview(null)} t={t} />}
    </div>
  );
}

function FileRowItem({ file, t, dir, onPreview }: {
  file: FileRow; t: (k: string, vars?: any) => string; dir: 'ltr' | 'rtl'; onPreview: () => void;
}) {
  const style = CAT_STYLE[file.category];
  const Icon = style.icon;

  return (
    <li>
      <button
        onClick={onPreview}
        className="group flex w-full items-start gap-3 px-4 py-3 text-start hover:bg-muted/40 transition-colors"
      >
        <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md ring-1', style.bg, style.text, style.ring)}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{file.filename}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
            <span>{file.date}</span>
            <span className="text-muted-foreground/40">·</span>
            <span>{t('pages_count', { n: file.pages })}</span>
            <span className="text-muted-foreground/40">·</span>
            <span>{file.sizeKb} KB</span>
            {file.important && (
              <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0 text-[9px] font-bold text-amber-700 dark:text-amber-300">DRAFT</span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Link
              to={`/client-pwa-v4/files/${file.id}/chat`}
              onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md bg-[var(--v4-accent)] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[var(--v4-accent-hover)]"
            >
              <MessageCircleQuestion className="h-3 w-3" />
              {t('ask_about_this')}
            </Link>
            <span className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
              <Download className="h-3 w-3" />
              {t('download')}
            </span>
          </div>
        </div>
        <ChevronRight className={cn('h-4 w-4 flex-shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground', dir === 'rtl' && 'rotate-180')} />
      </button>
    </li>
  );
}

function PreviewModal({ file, onClose, t }: { file: FileRow; onClose: () => void; t: (k: string, vars?: any) => string }) {
  const style = CAT_STYLE[file.category];
  const Icon = style.icon;
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-foreground/50 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="fixed inset-x-0 bottom-0 top-10 z-50 mx-auto max-w-[720px] overflow-hidden rounded-t-3xl bg-background shadow-2xl animate-in slide-in-from-bottom duration-300 flex flex-col">
        <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg', style.bg, style.text)}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">{file.filename}</p>
              <p className="text-[11px] text-muted-foreground">FY {file.fy} · {file.date} · {file.sizeKb} KB</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-3 flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:border-muted-foreground/40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-1 items-center justify-center p-8 bg-muted">
          <div className="text-center max-w-xs">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-lg bg-card shadow-sm border border-border">
              <FileText className="h-10 w-10 text-muted-foreground/60" />
            </div>
            <p className="mt-4 text-sm font-semibold text-foreground">{t('pdf_preview')}</p>
            <p className="mt-1 text-xs text-muted-foreground">PDF viewer would render here in production. For now this is a placeholder.</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 border-t border-border bg-card p-3">
          <Link
            to={`/client-pwa-v4/files/${file.id}/chat`}
            onClick={onClose}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-[var(--v4-accent)] py-3 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)]"
          >
            <MessageCircleQuestion className="h-4 w-4" />
            {t('ask_about_this')}
          </Link>
          <button className="flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card py-3 text-sm font-semibold text-foreground/80 hover:border-muted-foreground/40">
            <Eye className="h-4 w-4" />
            {t('open')}
          </button>
          <button className="flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card py-3 text-sm font-semibold text-foreground/80 hover:border-muted-foreground/40">
            <Download className="h-4 w-4" />
            {t('download')}
          </button>
        </div>
      </div>
    </>
  );
}

/** Inline grid cell on the Send hero — one of 4 action tiles. */
function SendAction({ onClick, icon: Icon, label }: { onClick: () => void; icon: any; label: string }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 rounded-md border border-border bg-card p-3 transition-all hover:border-[var(--v4-accent)] hover:bg-[var(--v4-accent-tint)] hover:-translate-y-0.5 active:scale-[0.97]"
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[var(--v4-accent)] text-white">
        <Icon className="h-4 w-4" />
      </span>
      <span className="text-[11px] font-semibold text-foreground leading-tight text-center">{label}</span>
    </button>
  );
}

/**
 * SendSheet — the rich "Send anything to your accountant" composer.
 * Four modes: camera · upload · voice · text. Mocked for the prototype.
 */
function AddFileSheet({ onClose, t }: { onClose: () => void; t: (k: string) => string }) {
  const [mode, setMode] = useState<'menu' | 'message' | 'voice' | 'sent'>('menu');
  const [msg, setMsg] = useState('');
  const [recordSec, setRecordSec] = useState(0);

  // Voice timer
  useEffect(() => {
    if (mode !== 'voice') return;
    const id = setInterval(() => setRecordSec(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [mode]);

  const send = () => { setMode('sent'); setTimeout(onClose, 1500); };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-[720px] rounded-t-2xl border-t border-border bg-card p-5 shadow-2xl animate-in slide-in-from-bottom duration-300">
        <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-muted" />

        {/* Menu */}
        {mode === 'menu' && (
          <>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-bold">{t('snap_receipt')}</h2>
              <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">{t('snap_hint')}</p>

            <div className="grid grid-cols-2 gap-2">
              <SheetOption icon={Camera}             title={t('opt_camera')}  hint={t('opt_camera_hint')}  onClick={send} />
              <SheetOption icon={Upload}             title={t('opt_upload')}  hint={t('opt_upload_hint')}  onClick={send} />
              <SheetOption icon={Mic}                title={t('opt_voice')}   hint={t('opt_voice_hint')}   onClick={() => setMode('voice')} />
              <SheetOption icon={MessageSquareText}  title={t('opt_message')} hint={t('opt_message_hint')} onClick={() => setMode('message')} />
            </div>

            {/* Tertiary channels — kept available but secondary */}
            <div className="mt-5 border-t border-border/60 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">{t('more_channels')}</p>
              <div className="space-y-1.5 text-[11px] text-muted-foreground">
                <p>📧 <span className="text-foreground">james-tiling-1234@receipts.ronidoc.com</span></p>
                <p>💬 WhatsApp <span className="text-foreground">+61 4XX XXX XXX</span></p>
              </div>
            </div>
          </>
        )}

        {/* Type a message */}
        {mode === 'message' && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold">{t('opt_message')}</h2>
              <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <textarea
              autoFocus
              value={msg}
              onChange={e => setMsg(e.target.value)}
              placeholder={t('type_your_message')}
              rows={5}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:border-[var(--v4-accent)] focus:outline-none resize-none"
            />
            <button
              onClick={send}
              disabled={!msg.trim()}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-[var(--v4-accent)] py-3 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)] disabled:opacity-50"
            >
              <Send className="h-4 w-4" /> {t('send_now')}
            </button>
          </>
        )}

        {/* Record voice */}
        {mode === 'voice' && (
          <>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-bold">{t('opt_voice')}</h2>
              <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-5">{t('opt_voice_hint')}</p>

            <div className="flex flex-col items-center py-6">
              <div className="relative flex h-24 w-24 items-center justify-center">
                <div className="absolute inset-0 animate-ping rounded-full bg-[var(--v4-accent)] opacity-20" />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[var(--v4-accent)] text-white shadow-lg">
                  <Mic className="h-8 w-8" />
                </div>
              </div>
              <p className="mt-5 text-2xl font-bold tabular-nums">{String(Math.floor(recordSec / 60)).padStart(2, '0')}:{String(recordSec % 60).padStart(2, '0')}</p>
              <p className="mt-1 text-xs uppercase tracking-widest text-[var(--v4-accent-text)] font-semibold">{t('recording')}</p>
            </div>
            <button
              onClick={send}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--v4-accent)] py-3 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)]"
            >
              <Send className="h-4 w-4" /> {t('send_now')}
            </button>
          </>
        )}

        {/* Sent confirmation */}
        {mode === 'sent' && (
          <div className="flex flex-col items-center py-10 text-center">
            <CheckCircle2 className="h-12 w-12 text-[var(--v4-accent)]" strokeWidth={2.5} />
            <p className="mt-3 text-base font-bold">{t('message_sent')}</p>
          </div>
        )}
      </div>
    </>
  );
}

function SheetOption({ icon: Icon, title, hint, onClick }: { icon: any; title: string; hint: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-start gap-2 rounded-md border border-border bg-card p-3 text-start hover:border-[var(--v4-accent)] hover:bg-[var(--v4-accent-tint)] transition-colors"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[var(--v4-accent)] text-white">
        <Icon className="h-4 w-4" />
      </span>
      <span>
        <p className="text-sm font-semibold text-foreground leading-tight">{title}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug">{hint}</p>
      </span>
    </button>
  );
}

export { FILES };
